import CodingalOffering from '@components/kids-teens-coding-camps/codingal-offering'
import Courses from '@components/kids-teens-coding-camps/courses'
import FirstFold from '@components/kids-teens-coding-camps/first-fold'
import VideoTestimonial from '@components/locale-pages/home/video-testimonial'
import Footer from '@components/common/footer'
import NavBar from '@components/common/navbar'
import Articles from '@components/home/articles'
import Faq from '@components/home/faq'
import MoneyBackGuarantee from '@components/home/guarantee'
import Teachers from '@components/home/teachers'
import Partners from '@components/locale-pages/home/us/partners'
import { VIDEO_TESTIMONIAL } from '@lib/data/summer-coding-camp'
import { FAQPageJsonLd, NextSeo } from 'next-seo'
import { formatSeoConfig } from '@lib/seo'
import { defineMessage, Trans } from '@lingui/macro'
import { FaqContentType } from '@lib/data/faq'
import TextImageCommonFold from '@components/common/text-image-common-fold'
import { Investors } from '@components/book-trial-class-jp/investors'
import TrustpilotReview from '@components/locale-pages/home/trustpilot-review'
import date from '@lib/utils/date.utils'
import { msgT } from '@lib/utils/i18n'

const WinterCodingCamp = formatSeoConfig({
  title: 'Online Winter Coding Camp for Kids & Teens',
  description:
    'Expert-led winter coding camp for kids aged 6–18. Hands-on projects, personalised attention and qualified instructors at Codingal. Try a free lesson.',
  url: 'https://www.codingal.com/kids-teens-coding-camps/winter/',
  metaImage: 'https://www.codingal.com/images/coding-camp/winter-og.jpg',
})
const couponCode = 'WINTER33'
const year = date().year()
export const FAQ_CONTENTS: FaqContentType[] = [
  {
    questionName: defineMessage({
      message: `Which is the best winter coding camp for kids and teens?`,
    }),
    acceptedAnswerText: (
      <>
        The best winter coding camp for kids and teens offers a comprehensive
        curriculum, personalised attention, and hands-on projects, ensuring an
        enriching and fun learning experience. Codingal’s winter coding camp is
        a top choice for its age-appropriate programs and expert instructors.
      </>
    ),
    acceptedAnswerTextString: defineMessage({
      message: `The best winter coding camp for kids and teens offers a comprehensive curriculum, personalised attention, and hands-on projects, ensuring an enriching and fun learning experience. Codingal’s winter coding camp is a top choice for its age-appropriate programs and expert instructors.`,
    }),
  },
  {
    questionName: defineMessage({
      message: `What can my child expect from Tech Kids Camp Winter ${year}?`,
    }),
    acceptedAnswerText: (
      <>
        Tech Kids Camp Winter {year} offers exciting online coding classes in
        Scratch, Python, and more. For just $99 a week, kids will enjoy hands-on
        projects and fun learning experiences. Register today to give your child
        a fantastic winter break filled with tech skills and creativity!
      </>
    ),
    acceptedAnswerTextString: defineMessage({
      message: `Tech Kids Camp Winter ${year} offers exciting online coding classes in Scratch, Python, and more. For just $99 a week, kids will enjoy hands-on projects and fun learning experiences. Register today to give your child a fantastic winter break filled with tech skills and creativity!`,
    }),
  },
  {
    questionName: defineMessage({
      message: `What is winter camp?`,
    }),
    acceptedAnswerText: (
      <>
        A winter camp is a seasonal program where kids engage in various
        activities, such as coding, sports, arts, and more, during their winter
        break. It provides a fun and educational experience, keeping kids active
        and learning.
      </>
    ),
    acceptedAnswerTextString: defineMessage({
      message: `A winter camp is a seasonal program where kids engage in various activities, such as coding, sports, arts, and more, during their winter break. It provides a fun and educational experience, keeping kids active and learning.`,
    }),
  },
  {
    questionName: defineMessage({
      message: `What is coding camp?`,
    }),
    acceptedAnswerText: (
      <>
        A coding camp is an educational program where kids and teens learn
        computer programming through interactive lessons, projects, and games.
        It’s designed to build coding skills in a fun and engaging environment.
      </>
    ),
    acceptedAnswerTextString: defineMessage({
      message: `A coding camp is an educational program where kids and teens learn computer programming through interactive lessons, projects, and games. It’s designed to build coding skills in a fun and engaging environment.`,
    }),
  },
  {
    questionName: defineMessage({
      message: `How can I enrol my child for a coding winter camp?`,
    }),
    acceptedAnswerText: (
      <>
        Enrol your child in a coding winter camp by visiting the camp’s website,
        selecting the appropriate program for your child’s age and skill level,
        and completing the registration process online.
      </>
    ),
    acceptedAnswerTextString: defineMessage({
      message: `Enrol your child in a coding winter camp by visiting the camp’s website, selecting the appropriate program for your child’s age and skill level, and completing the registration process online.`,
    }),
  },
  {
    questionName: defineMessage({
      message: `What is an online coding camp?`,
    }),
    acceptedAnswerText: (
      <>
        An online coding camp is a virtual program where kids learn coding
        through live, interactive sessions with instructors. It offers the
        flexibility of learning from home while still providing a structured and
        collaborative environment.
      </>
    ),
    acceptedAnswerTextString: defineMessage({
      message: `An online coding camp is a virtual program where kids learn coding through live, interactive sessions with instructors. It offers the flexibility of learning from home while still providing a structured and collaborative environment.`,
    }),
  },
  {
    questionName: defineMessage({
      message: `What is preschool coding?`,
    }),
    acceptedAnswerText: (
      <>
        Preschool coding involves introducing very young children to the basics
        of programming through games, puzzles, and simple interactive
        activities, helping them develop problem-solving skills and logical
        thinking early on.
      </>
    ),
    acceptedAnswerTextString: defineMessage({
      message: `Preschool coding involves introducing very young children to the basics of programming through games, puzzles, and simple interactive activities, helping them develop problem-solving skills and logical thinking early on.`,
    }),
  },
  {
    questionName: defineMessage({
      message: `What is a coding camp for middle schoolers?`,
    }),
    acceptedAnswerText: (
      <>
        A coding camp for middle schoolers focuses on teaching programming
        skills that are appropriate for their age and understanding. These camps
        often include more advanced topics and projects, helping students deepen
        their coding knowledge.
      </>
    ),
    acceptedAnswerTextString: defineMessage({
      message: `A coding camp for middle schoolers focuses on teaching programming skills that are appropriate for their age and understanding. These camps often include more advanced topics and projects, helping students deepen their coding knowledge.`,
    }),
  },
  {
    questionName: defineMessage({
      message: `Which are the best online coding camps for elementary students?`,
    }),
    acceptedAnswerText: (
      <>
        The best online coding camps for elementary students are designed to be
        engaging and age-appropriate, using platforms like Scratch and Blockly
        to introduce coding concepts in a fun and accessible way.
      </>
    ),
    acceptedAnswerTextString: defineMessage({
      message: `The best online coding camps for elementary students are designed to be engaging and age-appropriate, using platforms like Scratch and Blockly to introduce coding concepts in a fun and accessible way.`,
    }),
  },
  {
    questionName: defineMessage({
      message: `Are there coding camps available for high school students?`,
    }),
    acceptedAnswerText: (
      <>
        Yes, there are coding camps specifically designed for high school
        students, covering advanced topics like app development, artificial
        intelligence, and cybersecurity, preparing them for future careers in
        technology.
      </>
    ),
    acceptedAnswerTextString: defineMessage({
      message: `Yes, there are coding camps specifically designed for high school students, covering advanced topics like app development, artificial intelligence, and cybersecurity, preparing them for future careers in technology.`,
    }),
  },
  {
    questionName: defineMessage({
      message: `Which is the best youth coding camp?`,
    }),
    acceptedAnswerText: (
      <>
        The best youth coding camp is one that challenges teens with advanced
        programming concepts and real-world projects, preparing them for future
        tech careers. Look for camps with strong mentorship and project-based
        learning.
      </>
    ),
    acceptedAnswerTextString: defineMessage({
      message: `The best youth coding camp is one that challenges teens with advanced programming concepts and real-world projects, preparing them for future tech careers. Look for camps with strong mentorship and project-based learning.`,
    }),
  },
  {
    questionName: defineMessage({
      message: `What age groups can attend the winter coding camp?`,
    }),
    acceptedAnswerText: (
      <>
        Our winter coding camp welcomes kids and teens aged 6-18. We offer
        specialised programs tailored to different age groups, ensuring an
        enriching and age-appropriate learning experience for all participants.
      </>
    ),
    acceptedAnswerTextString: defineMessage({
      message: `Our winter coding camp welcomes kids and teens aged 6-18. We offer specialised programs tailored to different age groups, ensuring an enriching and age-appropriate learning experience for all participants.`,
    }),
  },
  {
    questionName: defineMessage({
      message: `Is prior coding experience required to join the winter coding camp?`,
    }),
    acceptedAnswerText: (
      <>
        No, beginners are welcome! Our winter coding camp for kids and teens is
        designed to accommodate all skill levels, from absolute beginners to
        more advanced learners. Each student receives personalised guidance
        suited to their experience level.
      </>
    ),
    acceptedAnswerTextString: defineMessage({
      message: `No, beginners are welcome! Our winter coding camp for kids and teens is designed to accommodate all skill levels, from absolute beginners to more advanced learners. Each student receives personalised guidance suited to their experience level.`,
    }),
  },
  {
    questionName: defineMessage({
      message: `What programming languages will participants learn at the winter coding camp?`,
    }),
    acceptedAnswerText: (
      <>
        Participants will explore a diverse range of programming languages and
        platforms, including Scratch, Python, Thunkable, Roblox Studio, Lua,
        HTML, CSS, JavaScript, and Data Science, providing them with a broad
        foundation in computer programming.
      </>
    ),
    acceptedAnswerTextString: defineMessage({
      message: `Participants will explore a diverse range of programming languages and platforms, including Scratch, Python, Thunkable, Roblox Studio, Lua, HTML, CSS, JavaScript, and Data Science, providing them with a broad foundation in computer programming.`,
    }),
  },
  {
    questionName: defineMessage({
      message: `How are the virtual sessions of the winter coding camp conducted?`,
    }),
    acceptedAnswerText: (
      <>
        Our winter coding camp conducts live, interactive sessions through video
        conferencing software built into the Codingal dashboard. Each session is
        led by an experienced instructor who provides real-time guidance and
        support, using a best-in-class platform that enhances collaborative and
        interactive learning.
      </>
    ),
    acceptedAnswerTextString: defineMessage({
      message: `Our winter coding camp conducts live, interactive sessions through video conferencing software built into the Codingal dashboard. Each session is led by an experienced instructor who provides real-time guidance and support, using a best-in-class platform that enhances collaborative and interactive learning.`,
    }),
  },
  {
    questionName: defineMessage({
      message: `Why choose Codingal’s winter camp for children?`,
    }),
    acceptedAnswerText: (
      <>
        Codingal’s winter camp for children offers engaging online coding
        lessons in Scratch, Python, and more, all for just $99 a week. With the
        option to take multiple courses, your child can explore various coding
        skills and stay excited about learning through winter!
      </>
    ),
    acceptedAnswerTextString: defineMessage({
      message: `Codingal’s winter camp for children offers engaging online coding lessons in Scratch, Python, and more, all for just $99 a week. With the option to take multiple courses, your child can explore various coding skills and stay excited about learning through winter!`,
    }),
  },
  {
    questionName: defineMessage({
      message: `What are some fun winter activities for 5-year-olds?`,
    }),
    acceptedAnswerText: (
      <>
        For 5-year-olds, winter activities can be both exciting and educational!
        Consider indoor arts and crafts, simple coding games, and winter-themed
        storytime. Engaging them in online coding classes can also be a great
        way to keep them entertained and learning during the winter months.
      </>
    ),
    acceptedAnswerTextString: defineMessage({
      message: `For 5-year-olds, winter activities can be both exciting and educational! Consider indoor arts and crafts, simple coding games, and winter-themed storytime. Engaging them in online coding classes can also be a great way to keep them entertained and learning during the winter months.`,
    }),
  },
  {
    questionName: defineMessage({
      message: `What should I look for in a winter camp for kindergarten kids?`,
    }),
    acceptedAnswerText: (
      <>
        Look for winter camps for kindergarten kids that offer a mix of fun and
        learning. Ideal programs include interactive activities like arts and
        crafts, storytelling, and introductory coding lessons. Codingal’s winter
        camps provide a great way for kindergarteners to explore basic coding
        concepts while enjoying a variety of engaging activities.
      </>
    ),
    acceptedAnswerTextString: defineMessage({
      message: `Look for winter camps for kindergarten kids that offer a mix of fun and learning. Ideal programs include interactive activities like arts and crafts, storytelling, and introductory coding lessons. Codingal’s winter camps provide a great way for kindergarteners to explore basic coding concepts while enjoying a variety of engaging activities.`,
    }),
  },
  {
    questionName: defineMessage({
      message: `What are some popular winter activities for children?`,
    }),
    acceptedAnswerText: (
      <>
        Popular winter activities for children include snowball fights,
        sledding, and cosy indoor crafts. Online coding classes are also a
        fantastic option, allowing kids to learn new skills in a fun and
        interactive way while staying warm at home.
      </>
    ),
    acceptedAnswerTextString: defineMessage({
      message: `Popular winter activities for children include snowball fights, sledding, and cosy indoor crafts. Online coding classes are also a fantastic option, allowing kids to learn new skills in a fun and interactive way while staying warm at home.`,
    }),
  },
  {
    questionName: defineMessage({
      message: `What should I consider when looking for the best winter camp for kids?`,
    }),
    acceptedAnswerText: (
      <>
        The best winter camps for kids combine fun activities with educational
        opportunities. Look for camps that offer interactive learning
        experiences, such as coding classes, along with traditional winter fun.
        Codingal’s winter camps provide engaging online coding courses and
        affordable pricing, making them an excellent choice for a productive
        winter break.
      </>
    ),
    acceptedAnswerTextString: defineMessage({
      message: `The best winter camps for kids combine fun activities with educational opportunities. Look for camps that offer interactive learning experiences, such as coding classes, along with traditional winter fun. Codingal’s winter camps provide engaging online coding courses and affordable pricing, making them an excellent choice for a productive winter break.`,
    }),
  },
  {
    questionName: defineMessage({
      message: `What are some creative winter camp ideas for kids?`,
    }),
    acceptedAnswerText: (
      <>
        Creative winter camp ideas for kids include themed coding projects,
        interactive science experiments, and virtual field trips. Codingal’s
        online winter camps offer a mix of fun and learning with engaging coding
        classes, perfect for keeping kids entertained and educated during the
        winter.
      </>
    ),
    acceptedAnswerTextString: defineMessage({
      message: `Creative winter camp ideas for kids include themed coding projects, interactive science experiments, and virtual field trips. Codingal’s online winter camps offer a mix of fun and learning with engaging coding classes, perfect for keeping kids entertained and educated during the winter.`,
    }),
  },
  {
    questionName: defineMessage({
      message: `How can I find a winter break camp for kids near me?`,
    }),
    acceptedAnswerText: (
      <>
        To find a winter break camp for kids near you, check local community
        centers or online listings. Alternatively, Codingal offers convenient
        online winter camps that are accessible from anywhere. For $99 per week,
        your child can enjoy top-notch coding classes and activities from home.
      </>
    ),
    acceptedAnswerTextString: defineMessage({
      message: `To find a winter break camp for kids near you, check local community centers or online listings. Alternatively, Codingal offers convenient online winter camps that are accessible from anywhere. For $99 per week, your child can enjoy top-notch coding classes and activities from home.`,
    }),
  },
  {
    questionName: defineMessage({
      message: `What types of activities are included in Codingal’s winter camps for kids?`,
    }),
    acceptedAnswerText: (
      <>
        Codingal’s winter camps for kids include a variety of engaging
        activities such as interactive coding lessons in Scratch and Python.
        Each week-long course costs $99 and features hands-on projects and fun
        coding challenges. Kids can also enrol in multiple courses to explore
        different coding topics.
      </>
    ),
    acceptedAnswerTextString: defineMessage({
      message: `Codingal’s winter camps for kids include a variety of engaging activities such as interactive coding lessons in Scratch and Python. Each week-long course costs $99 and features hands-on projects and fun coding challenges. Kids can also enrol in multiple courses to explore different coding topics.`,
    }),
  },
  {
    questionName: defineMessage({
      message: `Why choose Codingal’s winter camp for kids?`,
    }),
    acceptedAnswerText: (
      <>
        Codingal’s winter camp for kids offers a fantastic way to learn coding
        online through engaging, interactive classes. For $99 per week, kids can
        dive into exciting coding projects and take multiple courses. Our camps
        ensure children stay entertained and learn new skills throughout winter
        break.
      </>
    ),
    acceptedAnswerTextString: defineMessage({
      message: `Codingal’s winter camp for kids offers a fantastic way to learn coding online through engaging, interactive classes. For $99 per week, kids can dive into exciting coding projects and take multiple courses. Our camps ensure children stay entertained and learn new skills throughout winter break.`,
    }),
  },
  {
    questionName: defineMessage({
      message: `How can I find a winter camp for kids near me, and what are the options?`,
    }),
    acceptedAnswerText: (
      <>
        To find a winter camp for kids near you, search local community centres
        or online directories. For a flexible option, Codingal’s online winter
        camps offer engaging coding classes from home. At $99 per week, our
        courses cover various coding topics and are accessible to children
        everywhere.
      </>
    ),
    acceptedAnswerTextString: defineMessage({
      message: `To find a winter camp for kids near you, search local community centres or online directories. For a flexible option, Codingal’s online winter camps offer engaging coding classes from home. At $99 per week, our courses cover various coding topics and are accessible to children everywhere.`,
    }),
  },
  {
    questionName: defineMessage({
      message: `What can we expect from Codingal’s winter camp for kids in ${year}?`,
    }),
    acceptedAnswerText: (
      <>
        Codingal’s winter camp for kids in {year} features exciting online
        coding classes in topics like Scratch and Python. Each week-long course
        is priced at $99, with options to enrol in multiple courses. It’s a
        perfect way for kids to build coding skills and have fun while staying
        warm indoors.
      </>
    ),
    acceptedAnswerTextString: defineMessage({
      message: `Codingal’s winter camp for kids in ${year} features exciting online coding classes in topics like Scratch and Python. Each week-long course is priced at $99, with options to enrol in multiple courses. It’s a perfect way for kids to build coding skills and have fun while staying warm indoors.`,
    }),
  },
  {
    questionName: defineMessage({
      message: `What are some fun indoor winter camp activities for kids?`,
    }),
    acceptedAnswerText: (
      <>
        Indoor winter camp activities can be both entertaining and educational!
        Here are some great options: Coding Classes: Engage kids with
        interactive online coding lessons. Codingal’s winter camps offer fun
        projects in Scratch, Python, and more. Each week-long course is just
        $99, and kids can explore multiple coding topics. Arts and Crafts:
        Create winter-themed decorations or art projects. Interactive
        Storytelling: Enjoy engaging winter stories with a twist. Indoor Science
        Experiments: Explore simple, fun science activities.
      </>
    ),
    acceptedAnswerTextString: defineMessage({
      message: `Indoor winter camp activities can be both entertaining and educational! Here are some great options:
Coding Classes: Engage kids with interactive online coding lessons. Codingal’s winter camps offer fun projects in Scratch, Python, and more. Each week-long course is just $99, and kids can explore multiple coding topics.
Arts and Crafts: Create winter-themed decorations or art projects.
Interactive Storytelling: Enjoy engaging winter stories with a twist.
Indoor Science Experiments: Explore simple, fun science activities.`,
    }),
  },
  {
    questionName: defineMessage({
      message: `What can my child expect from Codingal’s winter camp for kids in Sharjah?`,
    }),
    acceptedAnswerText: (
      <>
        Codingal’s winter camp for kids in Sharjah features exciting online
        coding classes in Scratch and Python. At just $99 per week, children can
        enjoy interactive lessons and even take multiple courses to enhance
        their coding skills, all from the comfort of their home.
      </>
    ),
    acceptedAnswerTextString: defineMessage({
      message: `Codingal’s winter camp for kids in Sharjah features exciting online coding classes in Scratch and Python. At just $99 per week, children can enjoy interactive lessons and even take multiple courses to enhance their coding skills, all from the comfort of their home.`,
    }),
  },
  {
    questionName: defineMessage({
      message: `What makes Codingal’s winter camp for kids in Abu Dhabi stand out?`,
    }),
    acceptedAnswerText: (
      <>
        Codingal’s winter camp for kids in Abu Dhabi offers engaging online
        coding classes at a discounted rate of $99 per week. Kids can learn
        coding languages like Scratch and Python and have the option to take
        multiple courses, providing a comprehensive and enjoyable winter
        learning experience.
      </>
    ),
    acceptedAnswerTextString: defineMessage({
      message: `Codingal’s winter camp for kids in Abu Dhabi offers engaging online coding classes at a discounted rate of $99 per week. Kids can learn coding languages like Scratch and Python and have the option to take multiple courses, providing a comprehensive and enjoyable winter learning experience.`,
    }),
  },
  {
    questionName: defineMessage({
      message: `What can my child learn in Codingal’s winter camp for kids in Dubai?`,
    }),
    acceptedAnswerText: (
      <>
        In Codingal’s winter camp for kids in Dubai, your child will dive into
        fun coding topics like Scratch and Python. Our week-long online course
        is just $99. They can even take multiple courses to explore more coding
        skills. It’s a great way to keep them engaged and learning this winter!
      </>
    ),
    acceptedAnswerTextString: defineMessage({
      message: `In Codingal’s winter camp for kids in Dubai, your child will dive into fun coding topics like Scratch and Python. Our week-long online course is just $99. They can even take multiple courses to explore more coding skills. It’s a great way to keep them engaged and learning this winter!`,
    }),
  },
  {
    questionName: defineMessage({
      message: `How does Codingal’s winter online coding class benefit my child in Mississauga?`,
    }),
    acceptedAnswerText: (
      <>
        Codingal’s winter online coding classes for kids in Mississauga offer
        exciting lessons in Scratch and Python. At only $99 for a week-long
        course, it’s an affordable way for your child to develop valuable
        skills. They can also enrol in multiple courses to broaden their coding
        knowledge!
      </>
    ),
    acceptedAnswerTextString: defineMessage({
      message: `Codingal’s winter online coding classes for kids in Mississauga offer exciting lessons in Scratch and Python. At only $99 for a week-long course, it’s an affordable way for your child to develop valuable skills. They can also enrol in multiple courses to broaden their coding knowledge!`,
    }),
  },
  {
    questionName: defineMessage({
      message: `What’s included in Codingal’s winter coding camps for kids in Los Angeles?`,
    }),
    acceptedAnswerText: (
      <>
        Codingal’s winter coding camps for kids in Los Angeles feature cool
        topics like Roblox and Python. Each week-long course costs $99, and kids
        can join multiple classes to dive deeper into coding. It’s a fun and
        enriching way to make the most of winter break!
      </>
    ),
    acceptedAnswerTextString: defineMessage({
      message: `Codingal’s winter coding camps for kids in Los Angeles feature cool topics like Roblox and Python. Each week-long course costs $99, and kids can join multiple classes to dive deeper into coding. It’s a fun and enriching way to make the most of winter break!`,
    }),
  },
  {
    questionName: defineMessage({
      message: `How can my child join Codingal’s winter online coding camps in San Diego?`,
    }),
    acceptedAnswerText: (
      <>
        Joining Codingal’s winter online coding camps in San Diego is easy! Our
        week-long courses are $99 and cover topics like game development. Your
        child can even enrol in more than one course to explore different coding
        areas. It’s a fantastic way to keep them learning and having fun!
      </>
    ),
    acceptedAnswerTextString: defineMessage({
      message: `Joining Codingal’s winter online coding camps in San Diego is easy! Our week-long courses are $99 and cover topics like game development. Your child can even enrol in more than one course to explore different coding areas. It’s a fantastic way to keep them learning and having fun!`,
    }),
  },
  {
    questionName: defineMessage({
      message: `What are the details of Codingal’s winter online coding classes in Sacramento?`,
    }),
    acceptedAnswerText: (
      <>
        Codingal’s winter online coding classes in Sacramento are offered at $99
        for a week-long course. Kids will learn coding in Scratch and Python and
        can take multiple courses to expand their skills. It’s a great
        opportunity for them to stay engaged and learn something new this
        winter!
      </>
    ),
    acceptedAnswerTextString: defineMessage({
      message: `Codingal’s winter online coding classes in Sacramento are offered at $99 for a week-long course. Kids will learn coding in Scratch and Python and can take multiple courses to expand their skills. It’s a great opportunity for them to stay engaged and learn something new this winter!`,
    }),
  },
  {
    questionName: defineMessage({
      message: `What can my child expect from Codingal’s winter online coding camps in Chicago?`,
    }),
    acceptedAnswerText: (
      <>
        Codingal’s winter online coding camps for kids in Chicago provide
        engaging lessons in Python and Scratch. Each week-long course is $99,
        and your child can enrol in multiple classes to dive deeper into coding.
        It’s a fun and affordable way to make winter break educational and
        exciting!
      </>
    ),
    acceptedAnswerTextString: defineMessage({
      message: `Codingal’s winter online coding camps for kids in Chicago provide engaging lessons in Python and Scratch. Each week-long course is $99, and your child can enrol in multiple classes to dive deeper into coding. It’s a fun and affordable way to make winter break educational and exciting!`,
    }),
  },
  {
    questionName: defineMessage({
      message: `What do I need to know about Codingal’s winter online coding classes in Seattle?`,
    }),
    acceptedAnswerText: (
      <>
        Codingal’s winter online coding classes in Seattle are a great way for
        your child to learn coding from home. Each week-long course costs $99
        and includes topics like Scratch and Python. Plus, they can take
        multiple courses to explore different aspects of coding. It’s a perfect
        way to keep them engaged this winter!
      </>
    ),
    acceptedAnswerTextString: defineMessage({
      message: `Codingal’s winter online coding classes in Seattle are a great way for your child to learn coding from home. Each week-long course costs $99 and includes topics like Scratch and Python. Plus, they can take multiple courses to explore different aspects of coding. It’s a perfect way to keep them engaged this winter!`,
    }),
  },
  {
    questionName: defineMessage({
      message: `Which is the best coding camp in Orlando for kids?`,
    }),
    acceptedAnswerText: (
      <>
        The best coding camp in Orlando for kids offers a curriculum tailored to
        different age groups, experienced instructors, and engaging projects.
        Research local options or consider top-rated online coding camps if a
        suitable local camp isn’t available.
      </>
    ),
    acceptedAnswerTextString: defineMessage({
      message: `The best coding camp in Orlando for kids offers a curriculum tailored to different age groups, experienced instructors, and engaging projects. Research local options or consider top-rated online coding camps if a suitable local camp isn’t available.`,
    }),
  },
  {
    questionName: defineMessage({
      message: `Which is the best coding camp in Houston for kids?`,
    }),
    acceptedAnswerText: (
      <>
        The best coding camp in Houston for kids provides high-quality
        instruction, a low student-to-instructor ratio, and hands-on learning
        experiences. Codingal’s online camps are also a great alternative if
        you’re looking for flexible options.
      </>
    ),
    acceptedAnswerTextString: defineMessage({
      message: `The best coding camp in Houston for kids provides high-quality instruction, a low student-to-instructor ratio, and hands-on learning experiences. Codingal’s online camps are also a great alternative if you’re looking for flexible options.`,
    }),
  },
  {
    questionName: defineMessage({
      message: `Which is the best coding camp in Los Angeles for kids?`,
    }),
    acceptedAnswerText: (
      <>
        The best coding camp in Los Angeles for kids should offer a diverse
        curriculum, skilled instructors, and a supportive learning environment.
        Consider both local and online options to find the best fit for your
        child.
      </>
    ),
    acceptedAnswerTextString: defineMessage({
      message: `The best coding camp in Los Angeles for kids should offer a diverse curriculum, skilled instructors, and a supportive learning environment. Consider both local and online options to find the best fit for your child.`,
    }),
  },
  {
    questionName: defineMessage({
      message: `Which is the best coding camp in Raleigh NC for kids?`,
    }),
    acceptedAnswerText: (
      <>
        The best coding camp in Raleigh, NC, should provide personalised
        instruction, engaging activities, and a curriculum that grows with the
        student’s abilities. Explore both in-person and virtual options to find
        the right camp.
      </>
    ),
    acceptedAnswerTextString: defineMessage({
      message: `The best coding camp in Raleigh, NC, should provide personalised instruction, engaging activities, and a curriculum that grows with the student’s abilities. Explore both in-person and virtual options to find the right camp.`,
    }),
  },
  {
    questionName: defineMessage({
      message: `Which is the best coding camp in The Woodlands for kids?`,
    }),
    acceptedAnswerText: (
      <>
        The best coding camp in The Woodlands offers a curriculum tailored to
        different skill levels, experienced instructors, and a focus on hands-on
        projects. Online coding camps can also provide excellent alternatives.
      </>
    ),
    acceptedAnswerTextString: defineMessage({
      message: `The best coding camp in The Woodlands offers a curriculum tailored to different skill levels, experienced instructors, and a focus on hands-on projects. Online coding camps can also provide excellent alternatives.`,
    }),
  },
  {
    questionName: defineMessage({
      message: `What can kids expect from winter camps in Canada?`,
    }),
    acceptedAnswerText: (
      <>
        Winter camps in Canada offer a range of activities, from outdoor
        adventures to indoor creative projects. Kids can enjoy snow play, arts
        and crafts, and even coding classes. Codingal’s winter camps are
        available online, featuring interactive coding sessions that make
        learning fun and accessible during the chilly winter months.
      </>
    ),
    acceptedAnswerTextString: defineMessage({
      message: `Winter camps in Canada offer a range of activities, from outdoor adventures to indoor creative projects. Kids can enjoy snow play, arts and crafts, and even coding classes. Codingal’s winter camps are available online, featuring interactive coding sessions that make learning fun and accessible during the chilly winter months.`,
    }),
  },
  {
    questionName: defineMessage({
      message: `Are there coding camps available for kids in Atlanta?`,
    }),
    acceptedAnswerText: (
      <>
        Yes, there are several coding camps in Atlanta designed to teach kids
        programming skills in a fun and engaging way. These camps cover various
        topics such as game development, web design, and AI and Machine
        Learning.
      </>
    ),
    acceptedAnswerTextString: defineMessage({
      message: `Yes, there are several coding camps in Atlanta designed to teach kids programming skills in a fun and engaging way. These camps cover various topics such as game development, web design, and AI and Machine Learning.`,
    }),
  },
  {
    questionName: defineMessage({
      message: `What coding camps are available for kids in Chicago?`,
    }),
    acceptedAnswerText: (
      <>
        In Chicago, you can find a variety of coding camps for kids, offering
        courses in coding, AI and Machine Learning, and game design. These camps
        are available during summer and school breaks, providing hands-on
        learning experiences.
      </>
    ),
    acceptedAnswerTextString: defineMessage({
      message: `In Chicago, you can find a variety of coding camps for kids, offering courses in coding, AI and Machine Learning, and game design. These camps are available during summer and school breaks, providing hands-on learning experiences.`,
    }),
  },
  {
    questionName: defineMessage({
      message: `Can my child attend a coding camp in Cincinnati?`,
    }),
    acceptedAnswerText: (
      <>
        Yes, Cincinnati offers several coding camps for kids where they can
        learn programming languages, build apps, and explore computer science in
        a structured environment.
      </>
    ),
    acceptedAnswerTextString: defineMessage({
      message: `Yes, Cincinnati offers several coding camps for kids where they can learn programming languages, build apps, and explore computer science in a structured environment.`,
    }),
  },
  {
    questionName: defineMessage({
      message: `Where can I find a coding camp for kids in Delaware?`,
    }),
    acceptedAnswerText: (
      <>
        Delaware has coding camps for kids that focus on developing coding
        skills through interactive projects and challenges. These camps are
        available throughout the year.
      </>
    ),
    acceptedAnswerTextString: defineMessage({
      message: `Delaware has coding camps for kids that focus on developing coding skills through interactive projects and challenges. These camps are available throughout the year.`,
    }),
  },
  {
    questionName: defineMessage({
      message: `Are there coding camps available for kids in Indiana?`,
    }),
    acceptedAnswerText: (
      <>
        Yes, Indiana offers coding camps that teach kids the fundamentals of
        programming, game development, and more, providing a great opportunity
        to start their coding journey.
      </>
    ),
    acceptedAnswerTextString: defineMessage({
      message: `Yes, Indiana offers coding camps that teach kids the fundamentals of programming, game development, and more, providing a great opportunity to start their coding journey.`,
    }),
  },
  {
    questionName: defineMessage({
      message: `What options are there for coding camps for kids in Indianapolis?`,
    }),
    acceptedAnswerText: (
      <>
        Indianapolis has several coding camps for kids, offering courses in
        various programming languages, AI and Machine Learning, and game
        development, ideal for young tech enthusiasts.
      </>
    ),
    acceptedAnswerTextString: defineMessage({
      message: `Indianapolis has several coding camps for kids, offering courses in various programming languages, AI and Machine Learning, and game development, ideal for young tech enthusiasts.`,
    }),
  },
  {
    questionName: defineMessage({
      message: `Is there a coding camp for kids in London?`,
    }),
    acceptedAnswerText: (
      <>
        Yes, London hosts numerous coding camps for kids, covering topics from
        basic coding principles to advanced app development, providing a
        stimulating environment for learning.
      </>
    ),
    acceptedAnswerTextString: defineMessage({
      message: `Yes, London hosts numerous coding camps for kids, covering topics from basic coding principles to advanced app development, providing a stimulating environment for learning.`,
    }),
  },
  {
    questionName: defineMessage({
      message: `Where can my child attend a coding camp in Los Angeles?`,
    }),
    acceptedAnswerText: (
      <>
        Los Angeles offers a wide range of coding camps for kids, focusing on
        areas such as game design, web development, and AI and Machine Learning,
        catering to various age groups and skill levels.
      </>
    ),
    acceptedAnswerTextString: defineMessage({
      message: `Los Angeles offers a wide range of coding camps for kids, focusing on areas such as game design, web development, and AI and Machine Learning, catering to various age groups and skill levels.`,
    }),
  },
  {
    questionName: defineMessage({
      message: `Are there coding camps available for kids in New Jersey?`,
    }),
    acceptedAnswerText: (
      <>
        Yes, New Jersey has several coding camps that teach kids essential
        programming skills, preparing them for future technological endeavors.
      </>
    ),
    acceptedAnswerTextString: defineMessage({
      message: `Yes, New Jersey has several coding camps that teach kids essential programming skills, preparing them for future technological endeavors.`,
    }),
  },
  {
    questionName: defineMessage({
      message: `What coding camps are available for kids in NYC?`,
    }),
    acceptedAnswerText: (
      <>
        New York City offers diverse coding camps for kids, ranging from
        beginner coding classes to advanced game development and AI and Machine
        Learning courses.
      </>
    ),
    acceptedAnswerTextString: defineMessage({
      message: `New York City offers diverse coding camps for kids, ranging from beginner coding classes to advanced game development and AI and Machine Learning courses.`,
    }),
  },
  {
    questionName: defineMessage({
      message: `Are there coding camps for kids in Philadelphia?`,
    }),
    acceptedAnswerText: (
      <>
        Yes, Philadelphia offers coding camps where kids can learn coding, AI
        and Machine Learning, and app development through hands-on projects and
        collaboration with peers. For online learning, Codingal, based out of
        Dover, DE, offers a winter camp featuring beginner to intermediate
        courses, perfect for enhancing coding skills during the colder months.
      </>
    ),
    acceptedAnswerTextString: defineMessage({
      message: `Yes, Philadelphia offers coding camps where kids can learn coding, AI and Machine Learning, and app development through hands-on projects and collaboration with peers. For online learning, Codingal, based out of Dover, DE, offers a winter camp featuring beginner to intermediate courses, perfect for enhancing coding skills during the colder months.`,
    }),
  },
  {
    questionName: defineMessage({
      message: `Where can my child attend a coding camp in Pittsburgh?`,
    }),
    acceptedAnswerText: (
      <>
        Pittsburgh hosts several coding camps for kids, providing opportunities
        to learn programming, develop games, and explore the world of
        technology. Additionally, Codingal, headquartered in Dover, DE, offers
        an online winter camp with courses from beginner to intermediate levels,
        making it an excellent choice for winter learning.
      </>
    ),
    acceptedAnswerTextString: defineMessage({
      message: `Pittsburgh hosts several coding camps for kids, providing opportunities to learn programming, develop games, and explore the world of technology. Additionally, Codingal, headquartered in Dover, DE, offers an online winter camp with courses from beginner to intermediate levels, making it an excellent choice for winter learning.`,
    }),
  },
  {
    questionName: defineMessage({
      message: `What coding camps are available for kids in Portland?`,
    }),
    acceptedAnswerText: (
      <>
        Portland offers coding camps that introduce kids to coding concepts,
        game design, and AI and Machine Learning, helping them build a strong
        foundation in technology. For those interested in online learning,
        Codingal, based out of Dover, DE, offers a winter camp with courses
        designed for beginners to intermediate coders.
      </>
    ),
    acceptedAnswerTextString: defineMessage({
      message: `Portland offers coding camps that introduce kids to coding concepts, game design, and AI and Machine Learning, helping them build a strong foundation in technology. For those interested in online learning, Codingal, based out of Dover, DE, offers a winter camp with courses designed for beginners to intermediate coders.`,
    }),
  },
  {
    questionName: defineMessage({
      message: `Can my child attend a coding camp in Sacramento?`,
    }),
    acceptedAnswerText: (
      <>
        Yes, Sacramento has a variety of coding camps for kids, focusing on
        programming languages, web development, and creative coding projects.
        Additionally, Codingal, headquartered in Dover, DE, offers an online
        winter camp with courses tailored for beginners to intermediate levels,
        ideal for expanding coding skills during the winter break.
      </>
    ),
    acceptedAnswerTextString: defineMessage({
      message: `Yes, Sacramento has a variety of coding camps for kids, focusing on programming languages, web development, and creative coding projects. Additionally, Codingal, headquartered in Dover, DE, offers an online winter camp with courses tailored for beginners to intermediate levels, ideal for expanding coding skills during the winter break.`,
    }),
  },
  {
    questionName: defineMessage({
      message: `Are there coding camps available for kids in San Diego?`,
    }),
    acceptedAnswerText: (
      <>
        Yes, San Diego offers coding camps that teach kids essential tech
        skills, from coding basics to advanced game development and AI and
        Machine Learning. For a flexible online option, Codingal, based out of
        Dover, DE, provides a winter camp with courses ranging from beginner to
        intermediate levels, perfect for keeping kids engaged over the winter.
      </>
    ),
    acceptedAnswerTextString: defineMessage({
      message: `Yes, San Diego offers coding camps that teach kids essential tech skills, from coding basics to advanced game development and AI and Machine Learning. For a flexible online option, Codingal, based out of Dover, DE, provides a winter camp with courses ranging from beginner to intermediate levels, perfect for keeping kids engaged over the winter.`,
    }),
  },
  {
    questionName: defineMessage({
      message: `Where can my child find a coding camp in Tacoma?`,
    }),
    acceptedAnswerText: (
      <>
        Tacoma provides several coding camps for kids, where they can learn to
        code, develop apps, and explore various aspects of technology.
        Additionally, Codingal, headquartered in Dover, DE, offers an online
        winter camp with special courses designed for beginners to intermediate
        coders, making it a great option for winter learning.
      </>
    ),
    acceptedAnswerTextString: defineMessage({
      message: `Tacoma provides several coding camps for kids, where they can learn to code, develop apps, and explore various aspects of technology. Additionally, Codingal, headquartered in Dover, DE, offers an online winter camp with special courses designed for beginners to intermediate coders, making it a great option for winter learning.`,
    }),
  },
  {
    questionName: defineMessage({
      message: `Are there coding camps for kids in Henderson, NV?`,
    }),
    acceptedAnswerText: (
      <>
        Yes, Henderson, NV, offers coding camps that focus on teaching kids
        programming, game development, and AI and Machine Learning in a fun and
        engaging environment. Additionally, Codingal, based out of Dover, DE,
        provides an online winter camp with courses from beginner to
        intermediate levels, perfect for enhancing coding skills over the
        winter.
      </>
    ),
    acceptedAnswerTextString: defineMessage({
      message: `Yes, Henderson, NV, offers coding camps that focus on teaching kids programming, game development, and AI and Machine Learning in a fun and engaging environment. Additionally, Codingal, based out of Dover, DE, provides an online winter camp with courses from beginner to intermediate levels, perfect for enhancing coding skills over the winter.`,
    }),
  },
  {
    questionName: defineMessage({
      message: `What coding camps are available for kids in Katy, TX?`,
    }),
    acceptedAnswerText: (
      <>
        Katy, TX, hosts several coding camps for kids, offering courses in
        coding, game development, and other tech-related fields, perfect for
        young coders. For an online alternative, Codingal, headquartered in
        Dover, DE, offers a winter camp with courses ranging from beginner to
        intermediate levels, ideal for learning during the winter break.
      </>
    ),
    acceptedAnswerTextString: defineMessage({
      message: `Katy, TX, hosts several coding camps for kids, offering courses in coding, game development, and other tech-related fields, perfect for young coders. For an online alternative, Codingal, headquartered in Dover, DE, offers a winter camp with courses ranging from beginner to intermediate levels, ideal for learning during the winter break.`,
    }),
  },
  {
    questionName: defineMessage({
      message: `What coding camps are available for kids in Chicago?`,
    }),
    acceptedAnswerText: (
      <>
        Chicago offers a variety of coding camps for kids, covering topics such
        as game design, web development, and AI and Machine Learning, catering
        to different skill levels. Additionally, Codingal, based out of Dover,
        DE, provides an online winter camp featuring courses for beginners to
        intermediate coders, perfect for winter learning from home.
      </>
    ),
    acceptedAnswerTextString: defineMessage({
      message: `Chicago offers a variety of coding camps for kids, covering topics such as game design, web development, and AI and Machine Learning, catering to different skill levels. Additionally, Codingal, based out of Dover, DE, provides an online winter camp featuring courses for beginners to intermediate coders, perfect for winter learning from home.`,
    }),
  },
  {
    questionName: defineMessage({
      message: `Are there coding camps for kids in Houston?`,
    }),
    acceptedAnswerText: (
      <>
        Yes, Houston provides coding camps where kids can learn programming,
        build games, and explore the world of technology in a supportive
        environment. Codingal, headquartered in Dover, DE, also offers an online
        winter camp with courses designed for beginners to intermediate coders,
        making it an excellent option for enhancing coding skills during the
        winter.
      </>
    ),
    acceptedAnswerTextString: defineMessage({
      message: `Yes, Houston provides coding camps where kids can learn programming, build games, and explore the world of technology in a supportive environment. Codingal, headquartered in Dover, DE, also offers an online winter camp with courses designed for beginners to intermediate coders, making it an excellent option for enhancing coding skills during the winter.`,
    }),
  },
  {
    questionName: defineMessage({
      message: `How can I find a coding camp for kids near me?`,
    }),
    acceptedAnswerText: (
      <>
        You can find coding camps for kids in your area by searching online,
        checking local community centers, or visiting specialized coding schools
        that offer programs for children. Codingal, based out of Dover, DE, also
        provides an online winter camp with courses from beginner to
        intermediate levels, accessible to kids anywhere.
      </>
    ),
    acceptedAnswerTextString: defineMessage({
      message: `You can find coding camps for kids in your area by searching online, checking local community centers, or visiting specialized coding schools that offer programs for children. Codingal, based out of Dover, DE, also provides an online winter camp with courses from beginner to intermediate levels, accessible to kids anywhere.`,
    }),
  },
  {
    questionName: defineMessage({
      message: `Are there coding camps for kids in Lexington, KY?`,
    }),
    acceptedAnswerText: (
      <>
        Yes, Lexington, KY, offers coding camps where kids can learn the basics
        of coding, develop games, and explore various tech fields through
        hands-on activities. Additionally, Codingal, headquartered in Dover, DE,
        offers an online winter camp with courses from beginner to intermediate
        levels, perfect for expanding coding knowledge during the winter break.
      </>
    ),
    acceptedAnswerTextString: defineMessage({
      message: `Yes, Lexington, KY, offers coding camps where kids can learn the basics of coding, develop games, and explore various tech fields through hands-on activities. Additionally, Codingal, headquartered in Dover, DE, offers an online winter camp with courses from beginner to intermediate levels, perfect for expanding coding knowledge during the winter break.`,
    }),
  },
  {
    questionName: defineMessage({
      message: `Are there coding camps available for kids in Tulsa?`,
    }),
    acceptedAnswerText: (
      <>
        Yes, Tulsa offers coding camps where kids can learn programming, game
        development, and more, providing a fun and educational experience. For
        those interested in online learning, Codingal, based out of Dover, DE,
        provides a winter camp with courses ranging from beginner to
        intermediate levels, ideal for winter learning.
      </>
    ),
    acceptedAnswerTextString: defineMessage({
      message: `Yes, Tulsa offers coding camps where kids can learn programming, game development, and more, providing a fun and educational experience. For those interested in online learning, Codingal, based out of Dover, DE, provides a winter camp with courses ranging from beginner to intermediate levels, ideal for winter learning.`,
    }),
  },
  {
    questionName: defineMessage({
      message: `What coding camps are available for kids in Alpharetta?`,
    }),
    acceptedAnswerText: (
      <>
        Alpharetta has coding camps that teach kids programming skills, game
        design, and other tech-related topics, suitable for various age groups
        and skill levels. Additionally, Codingal, headquartered in Dover, DE,
        offers an online winter camp with specialized courses from beginner to
        intermediate levels, perfect for learning during the winter.
      </>
    ),
    acceptedAnswerTextString: defineMessage({
      message: `Alpharetta has coding camps that teach kids programming skills, game design, and other tech-related topics, suitable for various age groups and skill levels. Additionally, Codingal, headquartered in Dover, DE, offers an online winter camp with specialized courses from beginner to intermediate levels, perfect for learning during the winter.`,
    }),
  },
  {
    questionName: defineMessage({
      message: `Are there coding camps for kids in Cork?`,
    }),
    acceptedAnswerText: (
      <>
        Yes, Cork offers coding camps where kids can learn the basics of coding,
        create their own apps, and explore technology in an interactive setting.
        Additionally, Codingal, based out of Dover, DE, provides an online
        winter camp with courses from beginner to intermediate levels, perfect
        for enhancing coding skills during the winter months.
      </>
    ),
    acceptedAnswerTextString: defineMessage({
      message: `Yes, Cork offers coding camps where kids can learn the basics of coding, create their own apps, and explore technology in an interactive setting. Additionally, Codingal, based out of Dover, DE, provides an online winter camp with courses from beginner to intermediate levels, perfect for enhancing coding skills during the winter months.`,
    }),
  },
  {
    questionName: defineMessage({
      message: `Where can my child attend a coding camp in Dublin?`,
    }),
    acceptedAnswerText: (
      <>
        Dublin hosts several coding camps for kids, providing opportunities to
        learn programming, develop games, and engage with technology in a fun
        environment. Additionally, Codingal, headquartered in Dover, DE, offers
        an online winter camp with courses ranging from beginner to intermediate
        levels, making it an excellent choice for winter learning.
      </>
    ),
    acceptedAnswerTextString: defineMessage({
      message: `Dublin hosts several coding camps for kids, providing opportunities to learn programming, develop games, and engage with technology in a fun environment. Additionally, Codingal, headquartered in Dover, DE, offers an online winter camp with courses ranging from beginner to intermediate levels, making it an excellent choice for winter learning.`,
    }),
  },
  {
    questionName: defineMessage({
      message: `Are there coding camps for kids in Louisville?`,
    }),
    acceptedAnswerText: (
      <>
        Yes, Louisville offers coding camps where kids can learn to code, create
        games, and explore various aspects of computer science. Additionally,
        Codingal, based out of Dover, DE, offers an online winter camp with
        courses ranging from beginner to intermediate levels, providing a great
        opportunity for winter learning.
      </>
    ),
    acceptedAnswerTextString: defineMessage({
      message: `Yes, Louisville offers coding camps where kids can learn to code, create games, and explore various aspects of computer science. Additionally, Codingal, based out of Dover, DE, offers an online winter camp with courses ranging from beginner to intermediate levels, providing a great opportunity for winter learning.`,
    }),
  },
  {
    questionName: defineMessage({
      message: `What coding camps are available for kids in Nashville?`,
    }),
    acceptedAnswerText: (
      <>
        Nashville has coding camps that teach kids programming, game
        development, and more, providing a solid foundation in technology and
        creativity. For those interested in online learning, Codingal,
        headquartered in Dover, DE, offers a winter camp with courses designed
        for beginner to intermediate students, perfect for enhancing coding
        skills during the winter break.
      </>
    ),
    acceptedAnswerTextString: defineMessage({
      message: `Nashville has coding camps that teach kids programming, game development, and more, providing a solid foundation in technology and creativity. For those interested in online learning, Codingal, headquartered in Dover, DE, offers a winter camp with courses designed for beginner to intermediate students, perfect for enhancing coding skills during the winter break.`,
    }),
  },
  {
    questionName: defineMessage({
      message: `Are there coding camps for kids in Ottawa?`,
    }),
    acceptedAnswerText: (
      <>
        Yes, Ottawa offers coding camps for kids, where they can learn to code,
        create apps, and explore the world of technology in a fun and engaging
        way. Additionally, Codingal, based out of Dover, DE, provides an online
        winter camp with courses from beginner to intermediate levels, ideal for
        young learners looking to improve their coding skills during the winter.
      </>
    ),
    acceptedAnswerTextString: defineMessage({
      message: `Yes, Ottawa offers coding camps for kids, where they can learn to code, create apps, and explore the world of technology in a fun and engaging way. Additionally, Codingal, based out of Dover, DE, provides an online winter camp with courses from beginner to intermediate levels, ideal for young learners looking to improve their coding skills during the winter.`,
    }),
  },
  {
    questionName: defineMessage({
      message: `Where can my child find a coding camp in Toronto?`,
    }),
    acceptedAnswerText: (
      <>
        Toronto hosts numerous coding camps for kids, offering courses in
        programming, game development, and other tech-related fields, catering
        to all skill levels. For a flexible online option, Codingal,
        headquartered in Dover, DE, offers a winter camp with courses tailored
        for beginners to intermediate coders, providing a comprehensive learning
        experience from home.
      </>
    ),
    acceptedAnswerTextString: defineMessage({
      message: `Toronto hosts numerous coding camps for kids, offering courses in programming, game development, and other tech-related fields, catering to all skill levels. For a flexible online option, Codingal, headquartered in Dover, DE, offers a winter camp with courses tailored for beginners to intermediate coders, providing a comprehensive learning experience from home.`,
    }),
  },
  {
    questionName: defineMessage({
      message: `Where can my child attend a coding camp in Austin?`,
    }),
    acceptedAnswerText: (
      <>
        Austin offers a variety of kids coding camps that teach programming,
        game development, and AI and Machine Learning, helping kids build their
        tech skills in a supportive environment. For those seeking online
        options, Codingal, based out of Dover, DE, offers a winter camp with
        courses designed for beginner to intermediate students, making it a
        perfect choice for learning during the winter season.
      </>
    ),
    acceptedAnswerTextString: defineMessage({
      message: `Austin offers a variety of kids coding camps that teach programming, game development, and AI and Machine Learning, helping kids build their tech skills in a supportive environment. For those seeking online options, Codingal, based out of Dover, DE, offers a winter camp with courses designed for beginner to intermediate students, making it a perfect choice for learning during the winter season.`,
    }),
  },
  {
    questionName: defineMessage({
      message: `Are there coding camps for kids in Portland?`,
    }),
    acceptedAnswerText: (
      <>
        Portland offers coding camps that introduce kids to coding concepts,
        game design, and AI and Machine Learning, helping them build a strong
        foundation in technology. Additionally, Codingal, headquartered in
        Dover, DE, offers an online winter camp with courses designed for
        beginners to intermediate coders, providing a perfect opportunity for
        winter learning.
      </>
    ),
    acceptedAnswerTextString: defineMessage({
      message: `Portland offers coding camps that introduce kids to coding concepts, game design, and AI and Machine Learning, helping them build a strong foundation in technology. Additionally, Codingal, headquartered in Dover, DE, offers an online winter camp with courses designed for beginners to intermediate coders, providing a perfect opportunity for winter learning.`,
    }),
  },
]

const WinterCamp = () => {
  return (
    <>
      <NextSeo {...WinterCodingCamp} />
      <FAQPageJsonLd
        mainEntity={FAQ_CONTENTS.map(
          ({ acceptedAnswerTextString, questionName }) => ({
            acceptedAnswerText:
              typeof acceptedAnswerTextString === 'string'
                ? acceptedAnswerTextString
                : msgT(acceptedAnswerTextString),
            questionName:
              typeof questionName === 'string'
                ? questionName
                : msgT(questionName),
          })
        )}
      />
      <NavBar isSticky />
      <FirstFold
        breadcrumbItems={[
          { name: 'Codingal', link: '/' },
          { name: 'Coding camps', link: '/kids-teens-coding-camps/' },
          { name: 'Winter camp', link: '/kids-teens-coding-camps/winter/' },
        ]}
        title={<Trans>Winter Coding Camp for Kids & Teens {year}</Trans>}
        description={
          <div className="space-y-2">
            <p className="text-center lg:text-left text-sm lg:text-base">
              <Trans>
                Looking for educational and fun winter activities for kids and
                teens? <br />
                Check out Codingal’s Winter Camp for Kids & Teens {year}! Our
                camp offers a special winter edition of online coding classes
                with hands-on projects in Scratch, Python, Web & App
                Development, Roblox, and Data Science.
              </Trans>
            </p>
            <p className="text-center lg:text-left text-sm lg:text-base">
              <Trans>
                Each week-long course is just $99, providing an engaging way for
                your child or teen to explore coding and creativity.
              </Trans>
            </p>
          </div>
        }
        couponCode={couponCode}
        coursePrice={149}
        discountedPrice={99}
        offerPercentage={33}
        imagePath="/images/coding-camp/winter-first-fold-image"
        imageAlt="Code, Build and Hack with coding camp for kids and teens"
        backgroundColour="#0A12B0"
      />{' '}
      <Partners />
      <Courses
        couponCode={couponCode}
        title={
          <Trans>
            Choose from personalized Winter Coding Camps for your child
          </Trans>
        }
      />
      <VideoTestimonial
        title="What parents and students say about our winter coding camp"
        video_testimonial={VIDEO_TESTIMONIAL}
      />
      <TrustpilotReview isCampPage />
      <Teachers
        isShowBecomeTeacher={false}
        title="Learn coding from the best IT instructors"
        ctaLink="#courses"
        ctaText="Claim your spot now"
      />
      <CodingalOffering
        title={<Trans>Exclusive advantages of Winter Coding Camp</Trans>}
      />
      <MoneyBackGuarantee
        isCodingCampPage
        title="Why parents choose Codingal's winter coding camp"
      />
      <TextImageCommonFold
        title={
          <>
            A winter school program with{' '}
            <span className="yellow-underline">
              fun activities and lessons for kids
            </span>
          </>
        }
        description={
          <>
            <p>
              This winter, Codingal’s Winter Coding Camp is the perfect way for
              kids to have fun while learning all about coding! Through hands-on
              projects, students will not only learn coding but also create
              their own games, websites, and apps.
            </p>
            <p>
              Each course has both beginner and advanced levels, so whether your
              child is just starting out or ready to level up, there’s something
              for everyone! With six fun, 45-minute lessons per program, kids
              will dive into coding while building creativity, problem-solving
              skills, and confidence.
            </p>
            <p>
              Our interactive lessons create a fun, supportive environment where
              kids can grow and succeed. Whether they’re building their first
              game or designing a cool website, your child will gain awesome
              skills for the future—all while having a great time during the
              holidays!
            </p>
            <p>
              Make this winter unforgettable with Codingal’s Winter Coding
              Camp—where fun and learning go hand in hand!
            </p>
          </>
        }
        imagePath="/images/coding-camp/summer-camp-activities"
        imageWidth={370}
        imageHeight={373}
      />
      <TextImageCommonFold
        title={
          <>
            STEM.org Accredited{' '}
            <span className="yellow-underline">Educational Experience</span> and
            Certificates
          </>
        }
        description={
          <>
            <p>
              STEM.org is a highly respected organization in the U.S. that
              ensures top-quality education in Science, Technology, Engineering,
              and Mathematics.
            </p>
            <p>
              Our winter camp coding curriculum is STEM.org-accredited, so your
              child will experience a fun and high-quality learning adventure.
              We mix coding with important STEM concepts, making learning both
              exciting and educational. For example, when building a fun game or
              app, kids not only learn coding but also practice math and logical
              thinking. When creating projects in Scratch, they’ll get to
              explore cool scientific ideas in a creative and hands-on way.
            </p>
            <p>
              After completing the camp, students receive STEM.org-accredited
              certificates, which show off their hard work and new skills. They
              can proudly share these certificates with friends, family, or even
              on social media! This special recognition gives them a big
              advantage, helping them stand out in future school applications
              and career opportunities.
            </p>
          </>
        }
        imagePath="/images/coding-camp/winter-camp-certificate"
        imageWidth={370}
        imageHeight={370}
      />
      <TextImageCommonFold
        title={
          <>
            Unlock your{' '}
            <span className="yellow-underline">
              skills and earn coding certifications
            </span>
          </>
        }
        description={
          <>
            <p>
              Let your child make the most of their holidays with Codingal’s
              winter coding courses. Through fun, hands-on projects, they’ll
              learn real-world skills in coding languages like Scratch, Python,
              JavaScript, and more.
            </p>
            <p>
              At the end of the course, students receive an official STEM.org
              certification, recognizing their newly acquired technical
              expertise and problem-solving abilities. These certifications are
              more than just milestones—they’re a valuable addition to your
              child’s portfolio, preparing them for future academic
              opportunities and careers in technology.
            </p>
          </>
        }
        imagePath="/images/coding-camp/winter-camp-certificate-2"
        imageWidth={370}
        imageHeight={370}
      />
      <TextImageCommonFold
        title={
          <>
            The best winter camp for{' '}
            <span className="yellow-underline">
              kids to learn coding basics and fundamentals
            </span>
          </>
        }
        description={
          <>
            <p>
              Our Winter Coding Camp is the ultimate way for kids to dive into
              the exciting world of coding while having a blast during their
              holiday break!
            </p>
            <p>
              With hands-on projects, interactive lessons, and a focus on
              essential coding concepts, your child will master valuable skills
              in languages like Scratch, Python, and more. Whether they’re
              designing their first video game, building a website, or creating
              an app, our camp turns coding into an enjoyable and rewarding
              experience.
            </p>
            <p>
              Choose from over 12 thrilling programs, including Scratch
              Explorer, Scratch Champion, Python Foundations, Python Champion,
              Website Wizard, Website Champion, App Inventor, App Champion,
              Roblox Rookie, Roblox Champion, Data Science Intro, and Data
              Science Champion. Each program includes six engaging lessons,
              giving your child the chance to explore their favorite topics and
              excel through project-based learning.
            </p>
            <p>
              Best of all, every program comes with a STEM-accredited
              certificate, allowing kids to proudly showcase their newly
              acquired skills!
            </p>
            <p>
              The best part? They’ll learn at a pace that’s perfect for
              winter—engaging, manageable, and designed to keep them excited and
              motivated all season long!
            </p>
          </>
        }
        imagePath="/images/coding-camp/summer-camp-student-learning"
        imageWidth={370}
        imageHeight={373}
      />
      <TextImageCommonFold
        title={
          <>
            Our winter courses are appropriate for{' '}
            <span className="yellow-underline">
              high school, middle school,
            </span>{' '}
            and elementary school students
          </>
        }
        description={
          <>
            <p>
              Our Winter Coding Camps are designed for students of all
              ages—whether they’re just starting their coding journey in
              elementary school or exploring advanced topics in high school. Our
              courses, tailored for grades K-12, provide tailored learning
              experiences and challenges that evolve with your child’s skills.
            </p>
            <p>
              From mastering the basics of coding with Scratch for Game
              Develpment and Thunkable for Application Develpment and Roblox for
              3D Game Development to creating advanced projects in Python and
              HTML, CSS, JS for Web Development.
            </p>
            <p>
              At Codingal, our teachers create a supportive, engaging, and
              interactive learning environment. Our goal is to spark curiosity,
              foster creativity, and build the confidence your child needs to
              thrive in the digital age.
            </p>
          </>
        }
        imagePath="/images/coding-camp/winter-camp-school-kids"
        imageWidth={370}
        imageHeight={373}
      />
      <Faq
        title="Got questions? Find answers in our FAQ section"
        faqContents={FAQ_CONTENTS}
      />
      <Articles />
      <Investors isCodingCampPage />
      <Footer />
    </>
  )
}

export default WinterCamp
